import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Save } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import type { Company } from "../../api/types";

// 客戶建檔的懸浮視窗(業主原話:「客戶內要新增客戶,一個按鈕、跳出懸浮視窗,
// 欄位:公司名 行號 窗口/負責人 手機電話 市內電話 EMAIL 聯絡地址」)。
//
// 同一個視窗兼辦新增與編輯:帶 company 進來就是編輯,不帶就是新增。
// 兩件事刻意做成這樣:
//
// 1. **編輯走 PATCH,而且只送改過的欄位。** 整包送回去,等於把使用者這次沒看
//    的欄位也一起覆寫掉 —— 同一份客戶資料常常有兩個人在維護,誰後存誰就把
//    對方剛填的手機蓋回舊值。
// 2. **失敗絕不關閉對話框。** 統編撞名(409)是最常見的失敗,關掉等於把剛打
//    的七欄丟掉。後端那句話原樣顯示,人改一改就能再送一次。

/** 七個欄位的鍵。順序就是畫面上的順序,照業主原話排。 */
type CompanyField =
  | "name"
  | "tax_id"
  | "contact_name"
  | "contact_mobile"
  | "contact_phone"
  | "contact_email"
  | "address";

type CompanyForm = Record<CompanyField, string>;

interface FieldSpec {
  key: CompanyField;
  label: string;
  hint?: string;
  inputMode?: "text" | "tel" | "email";
}

// 「行號」就是統一編號(業主 2026-09-10 確認),所以標籤寫「統一編號」,
// 沿用既有的 tax_id,不另外開一個永遠對不起來的欄位。
const FIELDS: FieldSpec[] = [
  { key: "name", label: "公司名稱", hint: "必填" },
  { key: "tax_id", label: "統一編號", hint: "個人客戶可留空,有填就必須是唯一的" },
  { key: "contact_name", label: "窗口、負責人" },
  { key: "contact_mobile", label: "手機電話", inputMode: "tel" },
  { key: "contact_phone", label: "市內電話", inputMode: "tel" },
  { key: "contact_email", label: "EMAIL", inputMode: "email" },
  { key: "address", label: "聯絡地址" },
];

const EMPTY_FORM: CompanyForm = {
  name: "",
  tax_id: "",
  contact_name: "",
  contact_mobile: "",
  contact_phone: "",
  contact_email: "",
  address: "",
};

/**
 * EMAIL 的形狀檢查:有 @、@ 兩側都有東西、網域帶得出一個點。
 *
 * 刻意只擋明顯打錯的(少了網域、只打一半),不追求 RFC 5322 那種完整比對 ——
 * 過嚴會把合法但少見的位址擋在門外,那比放行更麻煩。空字串代表沒填,放行。
 */
export function isPlausibleEmail(raw: string): boolean {
  const value = raw.trim();
  if (value === "") return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** 把既有客戶攤成表單的七格字串。沒有值的欄位一律空字串,不留 undefined。 */
function toForm(company: Company | null | undefined): CompanyForm {
  if (!company) return { ...EMPTY_FORM };
  const form = { ...EMPTY_FORM };
  for (const { key } of FIELDS) form[key] = company[key] ?? "";
  return form;
}

/** 編輯時真正要送出去的那幾格:與原值不同的才進 body。 */
export function changedFields(form: CompanyForm, company: Company): Partial<CompanyForm> {
  const diff: Partial<CompanyForm> = {};
  for (const { key } of FIELDS) {
    const next = form[key].trim();
    if (next !== (company[key] ?? "")) diff[key] = next;
  }
  return diff;
}

export interface CompanyDialogProps {
  open: boolean;
  onClose: () => void;
  /** 有值就是編輯那一筆(走 PATCH),沒值就是新增(走 POST)。 */
  company?: Company | null;
  /** 存檔成功後把那一筆交出去,呼叫端可以直接拿去選取。 */
  onSaved?: (company: Company) => void;
  /** 新增時先帶好的公司名稱(報價單客戶欄的「新增客戶『…』」)。 */
  initialName?: string;
}

export function CompanyDialog({ open, onClose, company = null, onSaved, initialName = "" }: CompanyDialogProps) {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const editing = company !== null;

  const startForm = () => (company === null && initialName !== "" ? { ...toForm(null), name: initialName } : toForm(company));
  const [form, setForm] = useState<CompanyForm>(startForm);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // 每次開啟都重新灌一次:新增從空白開始,編輯帶著那一列現有的值,
  // 上一次打到一半的東西不會殘留到下一次。
  useEffect(() => {
    if (!open) return;
    setForm(startForm());
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, company, initialName]);

  // 開啟後把焦點送進第一格。**刻意不用 autoFocus**:那是在 commit 階段就搶焦點,
  // 早於 Dialog 記下「原本焦點在哪顆按鈕」,關閉後焦點就回不到觸發的按鈕上,
  // 鍵盤使用者會被丟回頁首從頭 Tab 一次。
  useEffect(() => {
    if (!open) return;
    nameRef.current?.focus();
  }, [open]);

  const setField = (key: CompanyField, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  // 公司名稱是唯一的必填。只打空白也算沒填,不然會建出一家叫「   」的公司。
  const canSubmit = form.name.trim() !== "";

  const save = useMutation({
    mutationFn: (): Promise<Company> => {
      if (company) return api.patch<Company>(`/companies/${company.id}`, changedFields(form, company));
      const body: CompanyForm = { ...EMPTY_FORM };
      for (const { key } of FIELDS) body[key] = form[key].trim();
      return api.post<Company>("/companies", body);
    },
    onSuccess: (saved) => {
      push(editing ? `已更新「${saved.name}」` : `已新增客戶「${saved.name}」`, "success");
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      onSaved?.(saved);
      onClose();
    },
    // 後端的原句照抄(例如統編撞名的 409),前端不自己另編一句。
    onError: (err) => setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。"),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || save.isPending) return;
    // EMAIL 格式擋在前端:打錯的位址送出去,後端只會存下一個永遠寄不到的地址。
    if (!isPlausibleEmail(form.contact_email)) {
      setError("EMAIL 格式不正確,請確認有 @ 與網域。");
      return;
    }
    setError(null);
    // 編輯而一格都沒改,就沒有送出去的必要,直接關掉。
    if (company && Object.keys(changedFields(form, company)).length === 0) {
      onClose();
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? "編輯客戶" : "新增客戶"}
      maxWidth="max-w-md"
      // 打到一半誤點背景就整份消失太傷,關閉一律走 Esc 或明確的取消 / 關閉鈕。
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {FIELDS.map((field, index) => (
          <Input
            key={field.key}
            label={field.label}
            name={field.key}
            hint={field.hint}
            inputMode={field.inputMode}
            autoComplete="off"
            ref={index === 0 ? nameRef : undefined}
            value={form[field.key]}
            onChange={(event) => setField(field.key, event.target.value)}
          />
        ))}

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={save.isPending}
            icon={
              editing ? <Save aria-hidden="true" size={16} /> : <Plus aria-hidden="true" size={16} />
            }
          >
            {editing ? "儲存變更" : "建立客戶"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
